import type { ComponentProps, MouseEventHandler, Ref } from "react";
import type { VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import { Slot, Slottable } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Button } from "../Button";
import { badgeVariants, type BadgeColor } from "./styles";

export interface BadgeAction {
  icon: LucideIcon;
  onClick: MouseEventHandler<HTMLButtonElement>;
  /** Tooltip text, and the button's accessible name — the icon carries no text of its own. */
  hoverLabel?: string;
}

type BadgeStyleProps = Omit<VariantProps<typeof badgeVariants>, "hasAction">;

// The action button's box and glyph per badge size. `Button`'s own `xs` size is the closest fit but
// is still built for a standalone control, so both are overridden here to sit inside the pill.
const ACTION_SIZE = {
  xs: "size-3 [&_svg:not([class*='size-'])]:size-2",
  sm: "size-3.5 [&_svg:not([class*='size-'])]:size-2.5",
  md: "size-4 [&_svg:not([class*='size-'])]:size-3",
  lg: "size-5 [&_svg:not([class*='size-'])]:size-3.5",
} as const;

export interface BadgeProps extends ComponentProps<"span">, BadgeStyleProps {
  ref?: Ref<HTMLSpanElement>;
  /**
   * Form factor — the badge's fill/border treatment, independent of its color. `soft` is a quiet
   * tinted fill that still reads as a surface, unlike the fill-less `outline`/`dashed` — reach for
   * it in dense lists where a column of solid pills would be a wall of saturated color.
   * @default "solid"
   */
  variant?: "solid" | "soft" | "outline" | "dashed";
  /**
   * Intent color, applied on top of the chosen `variant`. `success`/`warning`/`info` are the
   * status hues — prefer them over a hand-rolled `className` so tone stays consistent. `white` is
   * a literal, theme-fixed white for badges on a dark/colored/photo surface — it does not invert
   * in dark mode, so only use it where the backdrop is dark in both themes.
   * @default "primary"
   */
  color?: BadgeColor;
  /**
   * Overall scale — padding, type size, and the size of both icon children and the `actionProps`
   * button. `md` matches the surrounding body text density; reach for `sm` in dense rows (table
   * cells, tag lists), `xs` for the tightest chrome (inline counts, gutter markers), and `lg` when
   * the badge sits beside `text-sm`/`text-base` content. `xs` and `sm` also drop to a lighter font
   * weight than the larger sizes, so they recede in dense layouts.
   * @default "md"
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Renders a trailing icon button inside the badge — the supported way to make a badge
   * interactive (a dismissible tag, say). Sizing and tone are handled here so every actionable
   * badge matches; nest your own `Button` as a child only when this doesn't cover the case.
   */
  actionProps?: BadgeAction;
  /**
   * Merge the badge's props onto the child element (Radix `Slot`) instead of rendering a `<span>`.
   * The way to make a badge a link — pass an `<a>` or a router `Link` as the only child and the
   * badge becomes that element, so it is the focusable, correctly-ringed control and picks up the
   * variant's anchor hover tone.
   */
  asChild?: boolean;
}

/**
 * A small status/label pill. Two orthogonal axes — `variant` (form factor) and `color` (intent) —
 * compose freely. Pass `actionProps` to add a trailing icon button (dismiss, edit, …).
 *
 * @example
 * ```tsx
 * <Badge>Active</Badge>                                    // solid primary
 * <Badge color="success">Paid</Badge>                      // solid green
 * <Badge color="destructive">Failed</Badge>                // solid red
 * <Badge variant="outline" color="warning">Past due</Badge>
 * <Badge variant="dashed" color="muted">Add tag</Badge>    // placeholder affordance
 * <Badge variant="soft" color="success">Paid</Badge>       // tinted fill, quieter than solid
 *
 * // dismissible tag
 * <Badge variant="outline" color="secondary" actionProps={{ icon: X, hoverLabel: "Remove", onClick: remove }}>
 *   Cardiology
 * </Badge>
 *
 * // as a link — the badge renders as the anchor/Link itself
 * <Badge asChild variant="outline">
 *   <a href={url} target="_blank" rel="noopener noreferrer">Canvas reference</a>
 * </Badge>
 * ```
 */
export const Badge = ({
  className,
  variant = "solid",
  color = "primary",
  size = "md",
  actionProps,
  asChild,
  children,
  ref,
  ...props
}: BadgeProps) => {
  const ActionIcon = actionProps?.icon;
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      ref={ref}
      data-slot="badge"
      className={cn(badgeVariants({ variant, color, size, hasAction: !!actionProps }), className)}
      {...props}
    >
      <Slottable>{children}</Slottable>
      {!!ActionIcon && (
        <Button
          variant="ghost"
          color="muted"
          size="xs"
          iconOnly
          hoverLabel={actionProps.hoverLabel}
          onClick={actionProps.onClick}
          // `text-current` inherits whatever foreground the variant/color pair set, so the icon
          // stays legible on a solid fill without this needing to know each color's on-tone.
          className={cn(
            "rounded-sm bg-transparent text-current hover:bg-black/10 dark:hover:bg-white/20",
            ACTION_SIZE[size]
          )}
        >
          <ActionIcon />
        </Button>
      )}
    </Comp>
  );
};
